/**
 * <gldn-enneagram-slider> — scroll-driven front/back animated slider.
 *
 * As the user scrolls through the section, a progress value 0 → 1
 * is computed from the section's vertical position in the viewport.
 * The progress drives a CSS custom property (--gldn-enn-progress)
 * that the stylesheet uses for:
 *   - Left/right gradient opacity (focus shifts between halves)
 *   - Translating bar transform (slides left → right)
 *   - Mobile caption fade (which caption is active)
 *
 * No actual slide movement on desktop — just opacity + gradient.
 * Mobile fades between the two captions as progress crosses 0.5.
 *
 * Respects prefers-reduced-motion: locks progress to 0 (slide 1 active,
 * gradient on left, bar at start) and skips all watchers.
 */
class GldnEnneagramSlider extends HTMLElement {
  #rafId = null;
  #observerActive = false;
  #intersectionObserver = null;
  #lastProgress = -1;
  #mobileCaptions = [];

  connectedCallback() {
    this.#mobileCaptions = Array.from(this.querySelectorAll('[data-caption-mobile-index]'));

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Skip all motion; let CSS default styles apply.
      return;
    }

    // Observe the section to know when to listen to scroll.
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver(this.#onIntersect, {
        threshold: [0, 0.01],
      });
      this.#intersectionObserver.observe(this);
    } else {
      // Fallback: always listen.
      this.#startScrollWatch();
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#stopScrollWatch();
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.#startScrollWatch();
      } else {
        this.#stopScrollWatch();
      }
    }
  };

  #startScrollWatch() {
    if (this.#observerActive) return;
    this.#observerActive = true;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#scheduleUpdate();
  }

  #stopScrollWatch() {
    if (!this.#observerActive) return;
    this.#observerActive = false;
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
  }

  #onScroll = () => {
    this.#scheduleUpdate();
  };

  #scheduleUpdate() {
    if (this.#rafId != null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#applyProgress();
    });
  }

  #applyProgress() {
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    /* Progress runs while the section is in the viewport:
       0 when the section is fully visible at the bottom of the viewport
         (rect.top = vh - section_height — i.e., section bottom touches
         viewport bottom),
       1 when the top of the section reaches the top of the viewport
         (rect.top = 0).
       This way the bar travels its full distance during the time the
       section is on screen, instead of needing the section to scroll
       completely off the top.
       Fallback: if the section is taller than the viewport, span one
       viewport height instead. Clamped to [0, 1]. */
    const startTop = Math.max(vh - rect.height, 0);
    const span = startTop > 0 ? startTop : vh;
    let progress = (startTop - rect.top) / span;
    if (progress < 0) progress = 0;
    if (progress > 1) progress = 1;

    // Quantize lightly to avoid sub-pixel-driven re-renders.
    const rounded = Math.round(progress * 1000) / 1000;
    if (rounded === this.#lastProgress) return;
    this.#lastProgress = rounded;

    this.style.setProperty('--gldn-enn-progress', String(rounded));

    // Mobile caption switch — slide 2 active once progress crosses 0.5
    if (this.#mobileCaptions.length === 2) {
      const activeIdx = rounded >= 0.5 ? 1 : 0;
      this.#mobileCaptions.forEach((cap, i) => {
        if (i === activeIdx) {
          cap.setAttribute('data-active', '');
        } else {
          cap.removeAttribute('data-active');
        }
      });
    }
  }
}

if (!customElements.get('gldn-enneagram-slider')) {
  customElements.define('gldn-enneagram-slider', GldnEnneagramSlider);
}
